import { memo } from 'react';
import { AiReceptionistEventType } from '@weave/schema-gen-ts/dist/shared/phone/events/vair/v1/vair.pb';
import { i18next, Trans, useTranslation } from '@frontend/i18n';
import {
  AI_EVENT_BADGE_TYPE,
  AiEventBadge,
  AiEventBadgeWithReason,
  AiEventIcon,
  type EventLabelConfig,
} from '@frontend/phone-ui';
import { TextLink, useTooltip, Chip, Text } from '@frontend/design-system';

export const AiEventActivityComponent = ({ event, reason }: { event: AiReceptionistEventType; reason?: string }) => {
  const config = AI_EVENT_CONFIG[event];

  if (!config) return null;

  return <AiEventBadgeWithReason config={config} reason={reason} />;
};

export const AiEventActivityListComponent = ({ events }: { events: AiReceptionistEventType[] }) => {
  const sortedEvents = sortEventsByOrder(events);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--fabric-space-gap-xxs)',
      }}
    >
      {sortedEvents.map((conf) => (
        <AiEventBadgeWithReason key={conf.event} config={conf} />
      ))}
    </div>
  );
};

export const AiEventInfoTableCell = memo(
  ({
    events,
    onClick,
    trackingId,
  }: {
    events?: AiReceptionistEventType[];
    onClick: () => void;
    trackingId?: string;
  }) => {
    const sortedEvents = events ? sortEventsByOrder(events) : [];
    const config = sortedEvents[0];

    const { triggerProps, tooltipProps, Tooltip } = useTooltip();

    if (!config || !events) return '-';

    return (
      <div
        data-testid='ai-event-table-cell'
        style={{
          display: 'grid',
          gap: 'var(--fabric-space-gap-xxs)',
          gridTemplateColumns: sortedEvents.length > 1 ? 'max-content max-content' : 'max-content',
          alignItems: 'center',
        }}
        {...(sortedEvents.length > 1 ? triggerProps : {})}
      >
        <AiEventBadge config={config} />
        {sortedEvents.length > 1 && (
          <TextLink
            data-testid='ai-event-more-count'
            css={{ cursor: 'pointer' }}
            size='medium'
            onClick={onClick}
            trackingId={trackingId}
          >{`+${sortedEvents.length - 1}`}</TextLink>
        )}
        {sortedEvents.length > 1 && (
          <Tooltip
            {...tooltipProps}
            css={{
              background: 'white',

              '.tooltip-floating-arrow': {
                fill: 'white',
              },
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--fabric-space-gap-xxs)',
                fontWeight: 'normal',
              }}
            >
              {sortedEvents.map((conf) => (
                <AiEventBadge key={conf.event} config={conf} />
              ))}
            </div>
          </Tooltip>
        )}
      </div>
    );
  }
);

AiEventInfoTableCell.displayName = 'AiEventInfoTableCell';

export const AiEventInfoBadge = memo(
  ({ events, trackingId }: { events: AiReceptionistEventType[]; trackingId?: string }) => {
    const sortedEvents = sortEventsByOrder(events);
    const config = sortedEvents[0];

    const {
      triggerProps: primaryTriggerProps,
      tooltipProps: primaryTooltipProps,
      Tooltip: PrimaryTooltip,
    } = useTooltip();
    const {
      triggerProps: secondaryTriggerProps,
      tooltipProps: secondaryTooltipProps,
      Tooltip: SecondaryTooltip,
    } = useTooltip();

    if (!config) return null;

    return (
      <div
        style={{
          display: 'grid',
          gap: 'var(--fabric-space-gap-xxs)',
          gridTemplateColumns: sortedEvents.length > 1 ? 'max-content max-content' : 'max-content',
          alignItems: 'center',
        }}
      >
        <div {...primaryTriggerProps}>
          <AiEventIcon event={config.event} />
          <PrimaryTooltip
            {...primaryTooltipProps}
            css={{
              background: 'white',

              '.tooltip-floating-arrow': {
                fill: 'white',
              },
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--fabric-space-gap-xxs)',
                fontWeight: 'normal',
              }}
            >
              <AiEventBadge key={config.event} config={config} />
            </div>
          </PrimaryTooltip>
        </div>
        {sortedEvents.length > 1 && (
          <Chip
            {...secondaryTriggerProps}
            css={{ cursor: 'pointer' }}
            size='tiny'
            variant='neutral'
            data-trackingid={trackingId}
          >{`+${sortedEvents.length - 1}`}</Chip>
        )}
        {sortedEvents.length > 1 && (
          <SecondaryTooltip
            {...secondaryTooltipProps}
            css={{
              background: 'white',

              '.tooltip-floating-arrow': {
                fill: 'white',
              },
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--fabric-space-gap-xxs)',
                fontWeight: 'normal',
              }}
            >
              {sortedEvents.slice(1).map((conf) => (
                <AiEventBadge key={conf.event} config={conf} />
              ))}
            </div>
          </SecondaryTooltip>
        )}
      </div>
    );
  }
);

AiEventInfoBadge.displayName = 'AiEventInfoBadge';

/**
 * This function ALSO filters out events that are not applicable for display in the UI, marked with `event: AI_EVENT_BADGE_TYPE.UNUSED`.
 */
const sortEventsByOrder = (events: AiReceptionistEventType[]) =>
  events
    .map((event) => AI_EVENT_CONFIG[event])
    .filter((_config): _config is EventLabelConfig => _config !== undefined)
    .sort((a, b) => a.order - b.order);

type Translation = ReturnType<typeof useTranslation>['t'];

const aiEventCallPathDescriptionComponents = {
  strong: <Text weight='bold' size='medium' as='span' />,
  body: <Text as='span' size='medium' />,
};

export const getAiEventCallPathDescription = (event: string, t: Translation) => {
  switch (event) {
    // These events are not currently used in the UI, but we are keeping them here for future reference
    // case AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_ANSWERED:
    //   return (
    //     <Trans
    //       t={t}
    //       components={aiEventCallPathDescriptionComponents}
    //       defaults='<strong>AI Receptionist</strong> <body>transfers call to staff.</body>'
    //     />
    //   );
    // case AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_MISSED:
    //   return (
    //     <Trans
    //       t={t}
    //       components={aiEventCallPathDescriptionComponents}
    //       defaults='<strong>AI Receptionist</strong> <body>tried to transfer the caller to staff, but no one answered.</body>'
    //     />
    //   );
    // case AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_FATAL:
    //   return (
    //     <Trans
    //       t={t}
    //       components={aiEventCallPathDescriptionComponents}
    //       defaults='<strong>AI Receptionist</strong> <body>encountered a temporary error while handling the call.</body>'
    //     />
    //   );
    case AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_ADDED:
      return (
        <Trans
          t={t}
          components={aiEventCallPathDescriptionComponents}
          defaults='<strong>AI Receptionist</strong> <body>schedules appointment</body>'
        />
      );
    case AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CANCELLED:
      return (
        <Trans
          t={t}
          components={aiEventCallPathDescriptionComponents}
          defaults='<strong>AI Receptionist</strong> <body>cancels appointment</body>'
        />
      );
    case AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CONFIRMED:
      return (
        <Trans
          t={t}
          components={aiEventCallPathDescriptionComponents}
          defaults='<strong>AI Receptionist</strong> <body>confirms appointment</body>'
        />
      );
    case AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_RESCHEDULED:
      return (
        <Trans
          t={t}
          components={aiEventCallPathDescriptionComponents}
          defaults='<strong>AI Receptionist</strong> <body>reschedules appointment</body>'
        />
      );
    default:
      return null;
  }
};

export const AiEventCallPathDescription = ({ event }: { event: AiReceptionistEventType }) => {
  const { t } = useTranslation('calls');

  return getAiEventCallPathDescription(event, t);
};

const FULL_AI_EVENT_CONFIG: Record<string, EventLabelConfig> = {
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_ANSWERED]: {
    event: AI_EVENT_BADGE_TYPE.TRANSFERRED_TO_STAFF,
    text: i18next.t('Transferred to Staff', { ns: 'calls' }),
    order: 7,
    reason: i18next.t('Caller requested to speak directly with staff', { ns: 'calls' }),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_MISSED]: {
    event: AI_EVENT_BADGE_TYPE.TRANSFER_MISSED,
    text: i18next.t('Transfer Missed', { ns: 'calls' }),
    order: 1,
    reason: i18next.t('Caller requested to speak directly with staff', { ns: 'calls' }),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_FATAL]: {
    event: AI_EVENT_BADGE_TYPE.ERROR,
    text: i18next.t('Error', { ns: 'calls' }),
    order: 6,
    reason: i18next.t('System experienced temporary disruption', { ns: 'calls' }),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_NOT_APPLICABLE]: {
    event: AI_EVENT_BADGE_TYPE.UNUSED,
    text: '-',
    order: 9,
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_ADDED]: {
    event: AI_EVENT_BADGE_TYPE.SCHEDULE_ADDED,
    text: i18next.t('Added to Schedule', { ns: 'calls' }),
    order: 3,
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CANCELLED]: {
    event: AI_EVENT_BADGE_TYPE.SCHEDULE_CANCELLED,
    text: i18next.t('Cancelled', { ns: 'calls' }),
    order: 2,
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CONFIRMED]: {
    event: AI_EVENT_BADGE_TYPE.SCHEDULE_CONFIRMED,
    text: i18next.t('Confirmed', { ns: 'calls' }),
    order: 5,
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_REQUESTED]: {
    // Not currently used in the UI
    event: AI_EVENT_BADGE_TYPE.UNUSED,
    text: '-',
    order: 8,
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_RESCHEDULED]: {
    event: AI_EVENT_BADGE_TYPE.SCHEDULE_RESCHEDULED,
    text: i18next.t('Rescheduled', { ns: 'calls' }),
    order: 4,
  },
} as const;

const buildFilteredAiEventConfig = () => {
  const filteredTypes: Record<string, EventLabelConfig> = {};
  Object.entries(FULL_AI_EVENT_CONFIG).forEach(([key, config]) => {
    if (config.event !== AI_EVENT_BADGE_TYPE.UNUSED) {
      filteredTypes[key] = config;
    }
  });
  return filteredTypes;
};

export const AI_EVENT_CONFIG = buildFilteredAiEventConfig();
